import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface LogEntry {
  timestamp: string;
  message: string;
  level?: "info" | "warn" | "error" | "debug";
}

interface LogContextValue {
  log: string[];
  appendLog: (line: string, level?: "info" | "warn" | "error" | "debug") => void;
  clearLog: () => void;
}

const LogContext = createContext<LogContextValue | undefined>(undefined);

export const LogProvider = ({ children }: { children: ReactNode }) => {
  const [log, setLog] = useState<string[]>([]);

  const appendLog = useCallback((line: string, level: "info" | "warn" | "error" | "debug" = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    const levelPrefix = level !== "info" ? `[${level.toUpperCase()}] ` : "";
    const logLine = `${timestamp}  ${levelPrefix}${line}`;
    setLog(prev => [logLine, ...prev].slice(0, 200)); // Keep last 200 entries
  }, []);

  const clearLog = useCallback(() => {
    setLog([]);
  }, []);

  return (
    <LogContext.Provider value={{ log, appendLog, clearLog }}>
      {children}
    </LogContext.Provider>
  );
};

export const useLog = () => {
  const context = useContext(LogContext);
  if (!context) {
    throw new Error("useLog must be used within a LogProvider");
  }
  return context;
};
